"use client"

import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import type { FormSubmitState } from "./FormShell"

/**
 * The Egged Plus REST endpoints answer with a `BooleanResponseWrapper`
 * (`{ body: boolean, errorCode?: number, errorMessage?: string }`).
 * EGDE-452: rejections arrive either as `body: false` OR as `body: null`
 * with an errorCode/errorMessage (e.g. server-side validation) — only an
 * explicit `body: true` counts as success, otherwise the user saw the
 * success card for a failed submission.
 */
export function checkBooleanResponse(response: {
  data?: {
    body?: boolean | null
    errorCode?: number | null
    errorMessage?: string | null
  } | null
}) {
  const data = response.data
  if (!data || data.body !== true) {
    throw new Error(data?.errorMessage || "Submission failed")
  }
  return data
}

interface UseFormSubmitOptions<TValues> {
  mutationFn: (values: TValues) => Promise<unknown>
  /** Called after a successful submit and on retry — usually RHF `reset`. */
  onReset?: () => void
}

/**
 * Wraps react-query's mutation with the shared idle → success / error state
 * machine used by every form block.
 */
export function useFormSubmit<TValues>({
  mutationFn,
  onReset,
}: UseFormSubmitOptions<TValues>) {
  const [state, setState] = useState<FormSubmitState>("idle")

  const mutation = useMutation({
    mutationFn,
    onSuccess: () => {
      setState("success")
      onReset?.()
    },
    onError: () => {
      setState("error")
    },
  })

  return {
    state,
    isPending: mutation.isPending,
    submit: (values: TValues) => {
      setState("idle")
      mutation.mutate(values)
    },
    retry: () => {
      setState("idle")
      onReset?.()
    },
  }
}
